import { useBrandhubResourcesQuery } from '@brandhub/hooks/brandhubQueries';
import { useCurrentBrandHubDomainId } from '@brandhub/hooks/useCurrentBrandHubDomainId';
import { BrandhubResource } from '@brandhub/types/brandhub';
import { ErrorState } from '@components/ErrorState';
import Checkbox from '@semcore/ui/checkbox';
import { DataTable } from '@semcore/ui/data-table';
import Link from '@semcore/ui/link';
import Pagination from '@semcore/ui/pagination';
import Spin from '@semcore/ui/spin';
import SpinContainer from '@semcore/ui/spin-container';
import { Text } from '@semcore/ui/typography';
import { NoData } from '@semcore/ui/widget-empty';
import { useHasMultipleBrandsAccess } from '@src/hooks/useHasMultipleBrandsAccess';
import { CellRenderProps } from '@src/types/table';
import { formatDate } from '@src/utils/date';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 8;

interface BrandhubResourcesTableProps {
  selectedResource: BrandhubResource | null;
  onSelectedResourceChange: (resource: BrandhubResource) => void;
}

export const BrandhubResourcesTable = ({
  selectedResource,
  onSelectedResourceChange,
}: BrandhubResourcesTableProps) => {
  const { t } = useTranslation(['common', 'editor']);

  const [currentPage, setCurrentPage] = useState(1);
  const [brandHubDomainId] = useCurrentBrandHubDomainId();

  const hasMultipleBrandsAccess = useHasMultipleBrandsAccess();

  const { data, isPending, isPlaceholderData, isError, refetch } = useBrandhubResourcesQuery({
    page: currentPage,
    limit: PAGE_SIZE,
    ...(hasMultipleBrandsAccess && { brandHubDomainId }),
  });

  const resources = data?.resources ?? [];
  const totalPages = data?.pagination.totalPages ?? 1;

  const columns = [
    {
      name: 'checkbox',
      children: '',
      gtcWidth: 'min-content',
      alignItems: 'center',
    },
    {
      name: 'name',
      children: t('editor:resources.table.name'),
      gtcWidth: 'min-content',
      alignItems: 'center',
    },
    {
      name: 'file',
      children: t('editor:resources.table.file'),
      gtcWidth: 'minmax(152px, 1fr)',
      alignItems: 'center',
    },
    {
      name: 'createAt',
      children: t('editor:resources.table.createdAt'),
      gtcWidth: '140px',
      alignItems: 'center',
    },
  ];

  const renderCell = ({ columnName, rowIndex, defaultRender }: CellRenderProps) => {
    const resource = resources[rowIndex];

    if (!resource) {
      return defaultRender();
    }

    if (columnName === 'checkbox') {
      return (
        <Checkbox
          checked={selectedResource?.id === resource.id}
          data-test={`brandhub-resources-table-checkbox-${rowIndex}`}
          onChange={() => {
            onSelectedResourceChange(resource);
          }}
        />
      );
    }

    if (columnName === 'name') {
      return (
        <Text
          ellipsis
          className="max-w-32 min-w-16">
          {resource.name || '-'}
        </Text>
      );
    }

    if (columnName === 'file') {
      return (
        <Link
          className="max-w-full"
          href={resource.url}
          rel="noopener noreferrer"
          target="_blank">
          <Link.Text
            ellipsis
            className="max-w-full">
            {resource.url}
          </Link.Text>
        </Link>
      );
    }

    if (columnName === 'createAt') {
      return <Text>{formatDate(resource.createdAt)}</Text>;
    }

    return { alignItems: 'center' };
  };

  if (isPending) {
    return (
      <div className="flex h-full min-h-96 items-center justify-center p-3">
        <Spin size="xl" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        reloadMessage={t('common:reload')}
        onReload={refetch}
      />
    );
  }

  return (
    <div className="border-secondary flex flex-col overflow-hidden rounded-lg border">
      <SpinContainer loading={isPlaceholderData}>
        <DataTable
          aria-label={t('editor:resources.table.title')}
          columns={columns}
          data={resources}
          renderCell={renderCell}
          renderEmptyData={() => (
            <NoData
              className="mx-auto py-6"
              description={t('editor:resources.table.empty')}
              type="nothing-found"
            />
          )}
          uniqueRowKey="id"
          w="100%"
        />
      </SpinContainer>
      <div className="p-3">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onCurrentPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};
